import React from 'react';
import styled, { css } from 'styled-components';
import BackgroundImage, { Overlay } from 'gatsby-theme-core/src/components/background-image';
import Container from 'gatsby-theme-core/src/components/container';
import ColorBackground from './color';
import Padding from '../section/padding';
import BackgroundProvider from './context';

const getImagePosition = (imagePosition, imageRepeatSize) => {
  switch (imagePosition) {
    case 'container':
      return {
        $respectRatio: true,
      };
    case 'repeat':
      return {
        repeatBackground: true,
        $backgroundSize: imageRepeatSize,
      };
    case 'left_top':
      return {
        objectPosition: 'left top',
      };
    case 'left_center':
      return {
        objectPosition: 'left center',
      };
    case 'left_bottom':
      return {
        objectPosition: 'left bottom',
      };
    case 'center_30':
      return {
        objectPosition: 'center 30%',
      };
    case 'center_top':
      return {
        objectPosition: 'center top',
      };
    case 'center_center':
      return {
        objectPosition: 'center center',
      };
    case 'center_bottom':
      return {
        objectPosition: 'center bottom',
      };
    case 'right_top':
      return {
        objectPosition: 'right top',
      };
    case 'right_center':
      return {
        objectPosition: 'right center',
      };
    case 'right_bottom':
      return {
        objectPosition: 'right bottom',
      };
    case 'push_right_half':
      return {
        $push: 'right-half',
      };
    case 'push_left_half':
      return {
        $push: 'left-half',
      };
    default:
      return {};
  }
};

const getImageFit = (imageFit) => {
  switch (imageFit) {
    case 'cover':
      return {
        objectFit: 'cover',
      };
    case 'contain':
      return {
        objectFit: 'contain',
      };
    case 'fill':
      return {
        objectFit: 'fill',
      };
    case 'none':
      return {
        objectFit: 'none',
      };
    case 'scale_down':
      return {
        objectFit: 'scale-down',
      };
    default:
      return {};
  }
};

const getImageOverlay = (imageOverlay) => {
  switch (imageOverlay) {
    case 'lighten':
      return {
        lighten: true,
      };
    case 'darken':
      return {
        darken: true,
      };
    case 'darker':
      return {
        darker: true,
      };
    case 'standout':
      return {
        standout: true,
      };
    case 'fadeIn':
      return {
        fadeIn: true,
      };
    case 'fadeOut':
      return {
        fadeOut: true,
      };
    case 'fadeInAndOut':
      return {
        fadeInAndOut: true,
      };
    case 'fadeLeftToCenter':
      return {
        fadeLeftToCenter: true,
      };
    case 'fadeRightToCenter':
      return {
        fadeRightToCenter: true,
      };
    default:
      return {};
  }
};

const Vimeo = ({ vimeoCode }) => (
  <iframe
    title={`video ${vimeoCode}`}
    src={`https://player.vimeo.com/video/${vimeoCode}?background=1`}
    // width="640"
    // height="360"
    allowFullScreen
    allow="autoplay; fullscreen"
  />
);

const Youtube = ({
  youtubeCode,
  autoplay,
  mute,
  controls,
  loop,
}) => (
  <iframe
    title={`video ${youtubeCode}`}
    src={`https://www.youtube.com/embed/${youtubeCode}?autoplay=${autoplay ? 1 : 0}&mute=${mute ? 1 : 0}&controls=${controls ? 1 : 0}&loop=${loop}&playlist=${youtubeCode}`}
    allowFullScreen
    allow="autoplay"
  />
);
const LocalVideo = ({
  video,
  autoplay,
  mute,
  controls,
  loop,
}) => (
  <video
    controls={controls}
    autoPlay={autoplay}
    muted={mute}
    loop={loop}
    controlsList="nofullscreen nodownload noremoteplayback noplaybackrate"
    playsInline
    disablePictureInPicture
    disableRemotePlayback
  >
    <source src={video.url} type={video.mime} />
    <track kind="captions" />
  </video>
);

const getVideoComponent = ({ youtubeCode, vimeoCode, video }) => {
  if (vimeoCode) {
    return Vimeo;
  }
  if (youtubeCode) {
    return Youtube;
  }
  if (video) {
    return LocalVideo;
  }
  return () => null;
};

const Video = styled(({
  youtubeCode,
  vimeoCode,
  video,
  autoplay,
  mute,
  controls,
  loop,
  className,
}) => {
  const Component = getVideoComponent({ youtubeCode, vimeoCode, video });
  return (
    <div
      className={className}
    >
      <Component
        youtubeCode={youtubeCode}
        vimeoCode={vimeoCode}
        video={video}
        autoplay={autoplay}
        mute={mute}
        controls={controls}
        loop={loop}
      />
    </div>
  );
})`
  position: relative;
  z-index: -1;
  iframe, video {
    background-color: black;
    border: 0;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    height: 100%;
    aspect-ratio: ${({ aspectRatio }) => (aspectRatio === 'ratio4_3' ? '4 / 3' : '16 / 9')};
  }
`;

const Background = ({
  className,
  image,
  imagePosition,
  imageFit,
  imageClip,
  imageOverlay,
  imageRepeatSize,
  color,
  customColor,
  border,
  video,
  disableOnMobile,
  children,
}) => (
  <BackgroundProvider
    image={image}
    imagePosition={imagePosition}
    imageFit={imageFit}
    imageClip={imageClip}
    imageOverlay={imageOverlay}
    imageRepeatSize={imageRepeatSize}
    color={color}
    customColor={customColor}
    border={border}
  >
    <ColorBackground
      $disableOnMobile={disableOnMobile}
      className={className}
      color={color}
      customColor={customColor}
      border={border}
      imageClip={imageClip}
    >
      {video && (
        <Video {...video} />
      )}
      {!image?.localFile ? children : (
        <BackgroundImage
          className={className}
          image={image}
          {...getImageOverlay(imageOverlay)}
          {...getImagePosition(imagePosition, imageRepeatSize)}
          {...getImageFit(imageFit)}
        >
          {children}
        </BackgroundImage>
      )}
    </ColorBackground>
  </BackgroundProvider>
);

export default styled(Background)`
  ${({ video }) => video && css`
    & {
      display: flex;
      aspect-ratio: ${video.aspectRatio === 'ratio4_3' ? '4 / 3' : '16 / 9'};
      position: relative;
      ${Overlay}, ${Padding}, ${Container} {
        display: flex;
        flex-grow: 1;
        > {
          flex-grow: 1;
        }
      }
    }
  `}
  ${({ $size }) => ['flexHeight', 'fullHeight', 'almostFullHeight'].includes($size) && css`
    &, ${BackgroundImage} {
      display: flex;
      flex-grow: 1;
      ${Overlay}, ${Padding}, ${Container} {
        display: flex;
        flex-grow: 1;
        > {
          flex-grow: 1;
        }
      }
    }
  `}

  ${({ $size }) => ['fullHeight', 'almostFullHeight'].includes($size) && css`
    &, ${BackgroundImage} {
      align-items: stretch;
      justify-content: center;
      scroll-snap-align: start;
      min-height: 100vh;
      /* min-height: 100dvh; // this will make the page dance like crazy */
      ${$size === 'almostFullHeight' && css`
        min-height: 80vh;
      `}
    }
  `}

  // @TODO: we really make this mobile/desktop support better..
  ${({ disableOnMobile }) => disableOnMobile && css`
    @media all and (max-width: ${({ theme }) => theme.breakingpoints.md}) {
      .background-image {
        display: none;
      }
    }
  `}

  ${({ image }) => css`
    --light-theme-text-shadow: ${image ? '0 5px 10px rgba(0, 0, 0, 0.5)' : '0'};
  `}

`;
